import { useModeAwareQuery, useModeAwareMutation } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { useMode } from "@/providers/ModeProvider";
import { useCompany } from "@/contexts/CompanyContext";
import { useQueryClient } from "@tanstack/react-query";
import { mapTemplateListItemApiToUi, mapTemplateDetailApiToUi, mapTemplateUiToUpdateRequest, mapTemplateUiToCreateRequest } from "@/lib/mappers/templates";
import type { Template } from "@/types/template";
import type { GetTemplatesApiResponse, TemplateDetailApi, UpdateTemplateApiRequest, CreateTemplateApiRequest } from "@/types/api/templates";

export interface GetTemplatesParams extends Record<string, string | number | boolean | undefined> {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: string;
}

const mockTemplates: Template[] = [
  {
    id: '1',
    name: 'Payment Reminder',
    type: 'email',
    subject: 'Payment Reminder - Account {{customer.accountNumber}}',
    content: '<p>Dear {{customer.name}},</p><p>This is a friendly reminder that your payment of {{account.balance}} is due on {{account.dueDate}}.</p><p>Best regards,<br>{{company.name}}</p>',
    isDefault: true,
    category: ['payment', 'reminder'],
    variables: ['{{customer.name}}', '{{customer.accountNumber}}', '{{account.balance}}', '{{account.dueDate}}', '{{company.name}}'],
    lastModified: '2024-01-15T10:00:00Z',
    createdAt: '2024-01-01T10:00:00Z',
    createdBy: 'Admin User',
  },
  {
    id: '2',
    name: 'Welcome Email',
    type: 'email',
    subject: 'Welcome to {{company.name}}',
    content: '<p>Dear {{customer.name}},</p><p>Welcome to {{company.name}}! We are excited to have you as a customer.</p><p>Your account number is {{customer.accountNumber}}.</p>',
    isDefault: false,
    category: ['welcome', 'onboarding'],
    variables: ['{{customer.name}}', '{{customer.accountNumber}}', '{{company.name}}'],
    lastModified: '2024-01-20T14:30:00Z',
    createdAt: '2024-01-10T09:00:00Z',
    createdBy: 'Admin User',
  },
  {
    id: '3',
    name: 'Monthly Statement',
    type: 'statement',
    content: '<h1>Account Statement</h1><p>Account: {{customer.accountNumber}}</p><p>Customer: {{customer.name}}</p><p>Balance: {{account.balance}}</p><p>Due Date: {{account.dueDate}}</p>',
    isDefault: true,
    category: ['monthly', 'statement'],
    variables: ['{{customer.name}}', '{{customer.accountNumber}}', '{{account.balance}}', '{{account.dueDate}}'],
    lastModified: '2024-01-18T11:00:00Z',
    createdAt: '2024-01-05T10:00:00Z',
    createdBy: 'Admin User',
  },
  {
    id: '4',
    name: 'Overdue Notice',
    type: 'email',
    subject: 'Overdue Payment Notice',
    content: '<p>Dear {{customer.name}},</p><p>Your account {{customer.accountNumber}} is overdue. Please remit payment of {{account.balance}} immediately.</p>',
    isDefault: false,
    category: ['overdue', 'collections'],
    variables: ['{{customer.name}}', '{{customer.accountNumber}}', '{{account.balance}}'],
    lastModified: '2024-01-25T09:30:00Z',
    createdAt: '2024-01-12T10:00:00Z',
    createdBy: 'Admin User',
  },
  {
    id: '6',
    name: 'Payment Confirmation',
    type: 'email',
    subject: 'Payment Received - Thank You',
    content: '<p>Dear {{customer.name}},</p><p>We have received your payment of {{account.paymentAmount}}. Thank you!</p><p>Remaining balance: {{account.balance}}</p>',
    isDefault: false,
    category: ['payment', 'confirmation'],
    variables: ['{{customer.name}}', '{{account.paymentAmount}}', '{{account.balance}}'],
    lastModified: '2024-01-28T13:00:00Z',
    createdAt: '2024-01-15T10:00:00Z',
    createdBy: 'Admin User',
  },
  {
    id: '7',
    name: 'Final Notice',
    type: 'email',
    subject: 'FINAL NOTICE - Immediate Action Required - Account {{customer_accountNumber}}',
    content: '<p><strong>FINAL NOTICE - IMMEDIATE ACTION REQUIRED</strong></p><p>Dear {{contact_firstName}},</p><p>This is your <strong>FINAL NOTICE</strong> regarding the outstanding balance on your account {{account_number}}.</p><p><strong>Amount Due: {{account_balance}}</strong><br><strong>Days Overdue: {{days_overdue}}</strong></p><p>Despite our previous attempts to contact you, your account remains severely overdue. We must receive payment <strong>immediately</strong> to avoid further action.</p><p><strong>URGENT: Pay now using the secure link below:</strong><br>{{payment_link}}</p><p><strong>If payment is not received within 48 hours, we will be forced to:</strong></p><ul><li>Suspend your account and all services</li><li>Report this matter to credit reference agencies, which will negatively impact your credit rating</li><li>Commence legal proceedings to recover the debt, including additional costs and interest</li><li>Engage external debt collection agencies</li></ul><p>This is your last opportunity to resolve this matter before we escalate to formal legal action. We strongly urge you to make payment immediately or contact us to discuss payment arrangements.</p><p>If you have already made payment, please disregard this notice and contact us immediately with proof of payment.</p><p><strong>Contact us urgently:</strong><br>Email: {{creditController_email}}<br>Phone: {{company_phone}}</p><p>Failure to respond will result in immediate escalation.</p><p>{{creditController_firstName}}<br>Credit Control Department</p>',
    isDefault: false,
    category: ['final', 'collections', 'urgent'],
    variables: ['{{contact_firstName}}', '{{account_number}}', '{{account_balance}}', '{{days_overdue}}', '{{payment_link}}', '{{creditController_email}}', '{{company_phone}}', '{{creditController_firstName}}'],
    lastModified: '2024-01-30T16:00:00Z',
    createdAt: '2024-01-30T16:00:00Z',
    createdBy: 'Admin User',
  },
];

export function useTemplatesList() {
  const { apiGet } = useApi();
  const { selectedCompany } = useCompany();
  const { isDemo } = useMode();

  const fixedParams: GetTemplatesParams = {
    page: 1,
    pageSize: 100,
    sortBy: 'Name',
    sortOrder: 'asc',
  };

  return useModeAwareQuery<Template[], Error>(
    ["templates"],
    async ({ signal }) => {
      const response = await apiGet<GetTemplatesApiResponse>("/api/v1/templates", fixedParams, signal);
      return response.data.map(mapTemplateListItemApiToUi);
    },
    mockTemplates,
    {
      staleTime: 0,
      refetchOnMount: 'always',
      enabled: isDemo || !!selectedCompany?.id,
    }
  );
}

export function useTemplateById(templateId: string | null) {
  const { apiGet } = useApi();
  const mockTemplate = mockTemplates.find((t) => t.id === templateId) ?? null;

  return useModeAwareQuery<Template | null, Error>(
    ["template", templateId],
    async ({ signal }) => {
      if (!templateId) return null;
      const response = await apiGet<TemplateDetailApi>(`/api/v1/templates/${templateId}`, undefined, signal);
      return mapTemplateDetailApiToUi(response);
    },
    mockTemplate,
    {
      enabled: !!templateId,
      staleTime: 0,
      refetchOnMount: "always",
    }
  );
}

export function useCreateTemplate({ forceLive = false }: { forceLive?: boolean } = {}) {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<TemplateDetailApi, Error, Template>(
    async (data) => {
      const request: CreateTemplateApiRequest = mapTemplateUiToCreateRequest(data);
      return apiPost<TemplateDetailApi>("/api/v1/templates", request);
    },
    async (data) => {
      return {
        id: Date.now(),
        name: data.name,
        templateTypeId: data.type === "statement" ? 2 : 1,
        templateTypeName: data.type === "statement" ? "Statement" : "Email",
        subject: data.subject,
        content: data.content,
        isDefault: data.isDefault,
        category: data.category?.join(", "),
        variables: data.variables?.join(", "),
        createdAt: new Date().toISOString(),
        createdByUserName: "Current User",
      } as TemplateDetailApi;
    },
    {
      forceLive,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["templates"] });
      },
    }
  );
}

export function useUpdateTemplate(templateId: string, { forceLive = false }: { forceLive?: boolean } = {}) {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<TemplateDetailApi, Error, Template>(
    async (data) => {
      const request: UpdateTemplateApiRequest = mapTemplateUiToUpdateRequest(data);
      return apiPut<TemplateDetailApi>(`/api/v1/templates/${templateId}`, request);
    },
    async () => {
      return {
        id: Number(templateId),
        name: "Updated Template",
        templateTypeId: 1,
        templateTypeName: "Email",
        content: "",
        isDefault: false,
        createdAt: new Date().toISOString(),
      } as TemplateDetailApi;
    },
    {
      forceLive,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["templates"] });
        queryClient.invalidateQueries({ queryKey: ["template", templateId] });
      },
    }
  );
}

export function useDeleteTemplate() {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<void, Error, string>(
    async (templateId) => {
      await apiDelete(`/api/v1/templates/${templateId}`);
    },
    async () => {
      return;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["templates"] });
      },
    }
  );
}
